<script setup lang="ts">
import { ContentBlock } from '~/components/ui/content-block'
import type { TAboutBankHeader } from '~/components/widget/bank-page/about-bank-header'
import { useDateTimeFormat } from '~/composables/useDateTimeFormat'
import { AlertCircle, LucideEye } from 'lucide-vue-next'
import { WidgetBankPageAboutBankHeaderSkeleton } from '~/components/widget/bank-page'
import { Alert, AlertDescription } from '~/components/ui/alert'

const route = useRoute()

const props = defineProps<{
  bank?: TAboutBankHeader
}>()

const { t } = useI18n()

const pageSuffixMap: Record<string, string> = {
  'bank-bank-deposits': t('common.deposits'),
  'bank-bank-credits': t('common.credits'),
  'bank-bank-cards': t('common.bank_cards'),
  'bank-bank-news': t('common.news'),
  'bank-bank-rates': t('common.exchange_rates'),
  'bank-bank-vacancies': t('common.vacancies'),
  'bank-bank-branches': t('common.branches'),
  'bank-bank-money-transfers': t('common.money_transfers'),
  'bank-slug': ''
}

// Subpages show only the section name: the organization is already clear from the logo next to it
const pageTitle = computed(
  () => pageSuffixMap[useCleanRouteName(route.name) as string] || props.bank?.title || ''
)
</script>

<template>
  <div class="space-y-5">
    <Alert v-if="bank && bank.status !== 'active' && bank.status_comments" variant="red">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>
        <div v-hydrated-html="bank.status_comments" class="mt-0" />
      </AlertDescription>
    </Alert>
    <ContentBlock variant="white">
      <template v-if="!bank || Object.keys(bank).length === 0">
        <WidgetBankPageAboutBankHeaderSkeleton />
      </template>
      <article v-else class="space-y-6">
        <div class="flex flex-col justify-between gap-3 sm:gap-6 md:flex-row-reverse">
          <div :class="bank?.is_nbkr ? 'max-w-48 rounded-md' : 'h-16 rounded-md'">
            <template v-if="bank?.is_nbkr">
              <NuxtImg
                v-if="bank?.logo"
                :src="bank.logo"
                :alt="bank.title"
                class="w-32"
                fetchpriority="high"
              />
            </template>
            <template v-else>
              <NuxtImg
                v-if="bank?.logo_rectangle || bank?.logo"
                :src="bank.logo_rectangle || bank.logo"
                :alt="bank.title"
                class="h-full w-auto"
                fetchpriority="high"
              />
            </template>
          </div>
          <div>
            <Heading tag="h1" class="font-serif text-xl !leading-8 sm:!text-2xl md:!text-3xl">{{
              pageTitle
            }}</Heading>
            <div class="mt-1 text-xs leading-3 text-gray-600">
              <p>{{ bank?.official_title }}</p>
              <p class="mt-1">{{ bank?.license }}</p>
            </div>
          </div>
        </div>
        <slot />
        <div v-if="bank" class="flex justify-between border-t pt-6 align-middle text-sm">
          <div>
            {{ $t('common.info_updated') }}
            {{ useDateTimeFormat(bank.updated_at) }}
          </div>
          <div class="flex items-center"><LucideEye class="mr-2" /> {{ bank.views_count }}</div>
        </div>
      </article>
    </ContentBlock>
  </div>
</template>
